import {createSlice} from '@reduxjs/toolkit';

import aiAssistantProposalStatuses from '../../consts/aiAssistant/aiAssistantProposalStatuses';
import aiAssistantStates from '../../consts/aiAssistant/aiAssistantStates';
import storeSliceNames from '../../consts/storeSliceNames';

// Per-tab UI state: not persisted, not synced across tabs
const initialState = {
    isWidgetOpened: false,
    // As last sent by the backend - never derived on the client
    assistantState: aiAssistantStates.IDLE,
    // The open `action.proposed`, mapped to the app shape
    proposal: null,
};

const aiAssistantSlice = createSlice({
    name: storeSliceNames.aiAssistant,
    initialState,
    reducers: {
        // A new conversation starts from scratch
        openWidget() {
            return {...initialState, isWidgetOpened: true};
        },
        // Keeps the conversation so it stays visible while the widget fades out
        closeWidget(state) {
            state.isWidgetOpened = false;
        },
        setAssistantState(state, action) {
            state.assistantState = action.payload;
        },
        // Replaces an unanswered one - the widget shows one question at a time
        setProposal(state, action) {
            state.proposal = {
                ...action.payload,
                status: aiAssistantProposalStatuses.PENDING,
                confirmedSettingName: null,
                appliedValue: null,
            };
        },
        // First confirmation wins - a tap or a spoken "yes"
        setProposalConfirmed(state, action) {
            if (state.proposal?.status !== aiAssistantProposalStatuses.PENDING) return;

            state.proposal.status = aiAssistantProposalStatuses.APPLYING;
            state.proposal.confirmedSettingName = action.payload;
        },
        // Results for a replaced or dismissed proposal are ignored
        setProposalResult(state, action) {
            const {actionId, status, appliedValue = null} = action.payload;

            if (state.proposal?.actionId !== actionId) return;

            state.proposal.status = status;
            state.proposal.appliedValue = appliedValue;
        },
        clearProposal(state) {
            state.proposal = null;
        },
        resetAiAssistantState() {
            return {...initialState};
        },
    },
});

export const openWidget = () => aiAssistantSlice.actions.openWidget();
export const closeWidget = () => aiAssistantSlice.actions.closeWidget();
export const setAssistantState = (value) => aiAssistantSlice.actions.setAssistantState(value);
export const setProposal = (proposal) => aiAssistantSlice.actions.setProposal(proposal);
export const setProposalConfirmed = (settingName) => aiAssistantSlice.actions.setProposalConfirmed(settingName);
export const setProposalResult = ({actionId, status, appliedValue}) =>
    aiAssistantSlice.actions.setProposalResult({actionId, status, appliedValue});
export const clearProposal = () => aiAssistantSlice.actions.clearProposal();
export const resetAiAssistantState = () => aiAssistantSlice.actions.resetAiAssistantState();

export default aiAssistantSlice.reducer;
